import { Color, Emotion, Message } from "../Message";
import { color_mapping, hexToRgb } from "@/helpers/color_mappers";
import { realtimeManager } from "@/services/realtime/RealtimeManager";
import { RealtimePayload } from "@/services/realtime/types";
import { Robot } from "../Robot";
import { generateId } from "@/helpers/utils";
import { getFetchOptions } from "@/helpers/network_helpers";
import { CallerIdentity } from "@/types/RealtimeProtocol";
import { ChannelService } from "@/services/channel/ChannelService";

export class Peerbot implements Robot {
  id: string;
  isOn: boolean;
  errors: { time: Date; message: string; details: string }[] = [];
  kind = "Peerbot";
  isIPBased: boolean;
  connection: string;
  lastMessage: Message;
  status: string;
  caller: string;
  sessionId: string;

  constructor(contactInfo: string, caller: string = "") {
    this.id = "peerbot" + generateId();
    this.isOn = true;
    this.sessionId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `sess_${Math.random().toString(36).substring(2, 11)}`;
    const IPChecker = new RegExp(
      "^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$",
    );
    this.isIPBased = IPChecker.test(contactInfo);
    this.connection = contactInfo;
    this.lastMessage = {
      speech: "",
      title: "",
      color: "Light Blue",
      emotion: "Neutral",
    };
    this.status = "Connected";
    this.caller = caller;

    if (!this.isIPBased) {
      realtimeManager.setIdentifier(caller);
      realtimeManager.subscribe([this.connection.toLowerCase()]);
      this.requestSync();
    }
  }

  requestSync(): void {
    this._publish({ type: "requestSyncFace" });
  }

  sendMessage(message: Message): void {
    this._publish(this._getBehaviourPayload(message));
    this.lastMessage = message;
  }

  sendSpeech(speech: string): void {
    this.sendMessage({ ...this.lastMessage, speech });
  }

  sendEmotion(emotion: Emotion): void {
    this.sendMessage({ ...this.lastMessage, emotion });
  }

  sendColor(color: Color): void {
    this.sendMessage({ ...this.lastMessage, color });
  }

  sendBlink(): void {
    if (this.isIPBased) {
      fetch(
        `http://${this.connection}:8000/Blink`,
        getFetchOptions(
          {
            method: "POST",
            mode: "cors",
            body: "blink",
          },
          this.connection,
        ),
      )
        .then(() => {
          this.status = "Success";
        })
        .catch((e: Error) => {
          this._handleError("Could not send blink", e);
        });
    }
  }

  private _publish(payload: Record<string, unknown>): void {
    if (!this.connection) {
      console.error("Peerbot: Cannot publish, connection string is empty");
      return;
    }

    const callerIdentity: CallerIdentity = {
      uuid: this.caller.toLowerCase(),
      session_id: this.sessionId,
      name: this.caller,
      username: this.caller.includes("@") ? null : this.caller,
      email: this.caller.includes("@") ? this.caller : null,
      call_as: this.caller.includes("@") ? "email" : "username",
    };

    const toSend = {
      ...payload,
      sender: callerIdentity,
      senderID: this.caller,
      senderSessionId: this.sessionId,
    };

    if (this.isIPBased) {
      fetch(
        `http://${this.connection}:8000/SetBehaviour`,
        getFetchOptions(
          {
            method: "POST",
            mode: "no-cors",
            body: JSON.stringify(toSend),
          },
          this.connection,
        ),
      )
        .then(() => {
          this.status = "Success";
        })
        .catch((e: Error) => {
          this._handleError("IP send failed", e);
        });
    } else {
      realtimeManager
        .publish(this.connection, toSend as unknown as RealtimePayload)
        .catch((e: Error) => {
          this._handleError("Realtime publish failed", e);
        });
    }
  }

  private _getBehaviourPayload(message: Message) {
    const rgb = hexToRgb(color_mapping[message.color]);
    return {
      ...message,
      ...rgb,
      volume: 1,
      rate: 1,
      pitch: 1,
      gazeX: 0,
      gazeY: 0,
      goal: "",
      subgoal: "",
      proficiency: "",
    };
  }

  private _handleError(message: string, e: Error) {
    console.error(`Peerbot: ${message}`, e);
    this.errors = [
      ...this.errors,
      {
        time: new Date(),
        message: `${message} - ${e.message}`,
        details: JSON.stringify(e),
      },
    ];
    this.status = "Error";
  }

  disconnect(): void {
    if (!this.connection) return;
    const targetChannel = this.connection.trim().toLowerCase();
    const callerId = this.caller;

    // Clean up caller from Firestore approved_senders
    ChannelService.endCall(targetChannel, callerId).catch((err) => {
      console.warn(
        `Peerbot: endCall failed in ChannelService for ${targetChannel}:`,
        err,
      );
    });

    // Notify Face via realtime so it removes caller immediately
    const callerIdentity: CallerIdentity = {
      uuid: this.caller.toLowerCase(),
      session_id: this.sessionId,
      name: this.caller,
      username: this.caller.includes("@") ? null : this.caller,
      email: this.caller.includes("@") ? this.caller : null,
      call_as: this.caller.includes("@") ? "email" : "username",
    };

    const payload = {
      type: "end_call",
      caller: callerIdentity,
      sender: callerIdentity,
      senderID: this.caller,
      senderSessionId: this.sessionId,
    } as unknown as RealtimePayload;

    realtimeManager.publish(targetChannel, payload).catch((err) => {
      console.warn(
        `Peerbot: end_call publish failed for channel ${targetChannel}:`,
        err,
      );
    });

    if (!this.isIPBased && this.connection) {
      realtimeManager.unsubscribe([this.connection.toLowerCase()]);
    }
  }

  toString(): string {
    return JSON.stringify({
      kind: this.kind,
      caller: this.caller,
      contactInfo: this.connection,
    });
  }

  toPrettyString(): string {
    return `Peerbot connected to ${this.connection}`;
  }
}
